import { colors } from "@indeed/ifl-tokens/react-native";
import {
  AvroOptional,
  log as logReceivedFlag,
} from "@indeed/signals-schema/com/indeed/signals/schema/profile/MobRcvFlag";
import React, { useCallback, useEffect } from "react";
import { Platform, StyleSheet } from "react-native";

// NOTE: The proctor imports below require running the proctor codegen after adding mob_vj_apply_rate_limit_tst.json
// Run the appropriate build/codegen command to generate these types
import { Box, Heading, TextLink } from "../../../../../../mads";
import { Error, NewWindow } from "../../../../../../mads/icons";
import { useI18n } from "../../../../../../shared/I18n";
import { JSTLandingScreen } from "../../../../../../shared/native-modules/tare/JSTLandingScreen";
import navigateTo from "../../../../../../shared/native-modules/tare/navigateTo";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { MobVjApplyRateLimitTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useDomainUrl, useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

/**
 * Hook to determine if the apply limit banner should be visible
 *
 * This checks two conditions:
 * 1. The proctor mob_vj_apply_rate_limit_tst is in the "active" bucket (value === 1)
 * 2. The GraphQL flag applyRateLimit.isActive is true (returned from backend)
 */
export function useIsApplyLimitBannerVisible(): boolean {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const proctorAllocation = useViewjobProctorAllocation(MobVjApplyRateLimitTst);

  // Check if proctor is active (value === 1)
  const isProctorActive = proctorAllocation.value === 1;

  // Check if the flag from GraphQL is true
  // NOTE: This assumes the backend will add applyRateLimit { isActive } to the viewjob GraphQL response
  const isRateLimitActive = viewjobOneGraphModel?.jobSeeker?.applyRateLimit ?? false;

  return isProctorActive && isRateLimitActive;
}

/**
 * Banner component that displays when apply rate limiting is active
 */
export function ApplyLimitBanner(): React.JSX.Element | null {
  const i18n = useI18n();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const shouldShowBanner = useIsApplyLimitBannerVisible();
  const domain = useDomainUrl();

  // `domain` is a full origin (e.g. "https://www.indeed.com") from getDomain(url),
  // so do NOT prepend a scheme here — that would produce "https://https://...".
  const tosUrl = `${domain}/legal#tos:~:text=Limits%3A%20To,tooling%20is%20prohibited.`;

  const handleOpenLink = useCallback(async () => {
    // Web renders a real <a href> via TextLink's `targetUrl` and lets the browser
    // navigate, so this native-only path opens the in-app browser via the TARE bridge.
    if (Platform.OS === "web") {
      return;
    }
    try {
      await navigateTo(JSTLandingScreen.supportUrl(tosUrl), "RnViewjob");
    } catch (error) {
      console.error("Failed to open ToS link:", error);
    }
  }, [tosUrl]);

  useEffect(() => {
    if (shouldShowBanner && viewjobOneGraphModel?.mobvjtk) {
      logReceivedFlag({
        mobtk: viewjobOneGraphModel.mobvjtk,
        parentEventType: "mobviewjob",
        flag: AvroOptional.string("applyLimitBannerShown"), // Logged on render
        ts: AvroOptional.long(new Date().getTime()),
      });
    }
  }, [shouldShowBanner, viewjobOneGraphModel?.mobvjtk]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box sx={styles.container} id="apply-limit-banner">
      <Box sx={styles.contentContainer}>
        <Box sx={styles.iconContainer}>
          <Error size="sm" variant="filled" fill={colors.foregroundStatusCritical} />
        </Box>
        <Box sx={styles.textContainer}>
          <Heading level={2} sx={styles.heading}>
            {i18n.gettext("You've reached your daily apply limit")}
          </Heading>
          <TextLink
            level={2}
            // On web, `targetUrl` makes TextLink render an <a href> and the browser
            // handles navigation. On native, leave it undefined so `onPress` fires and
            // opens the in-app browser (avoids TextLink's native Linking.openURL path).
            targetUrl={Platform.OS === "web" ? tosUrl : undefined}
            target="_blank"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- fire and forget navigation
            onPress={handleOpenLink}
            iconAfter={<NewWindow />}
            aria-label={i18n.gettext("Learn more about apply limits (opens in new window)")}
          >
            {i18n.gettext("Learn more")}
          </TextLink>
        </Box>
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.backgroundStatusCriticalWeaker,
    borderRadius: 16,
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 24,
    marginStart: 16,
    marginEnd: 16,
  },
  contentContainer: {
    flexDirection: "row",
  },
  textContainer: {
    paddingHorizontal: 16,
  },
  heading: {
    color: colors.foregroundStrong,
    paddingBottom: 4,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: 2,
  },
});
